import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import {
  IxReleaseBanner,
  IxReleaseInfoBanner,
  IxReleaseWarningBanner,
  ReleaseBannerType,
} from '../../core/services/feature/release-banner.service';

@Component({
  selector: 'app-release-banner',
  templateUrl: './release-banner.component.html',
  styleUrl: './release-banner.component.scss',
  imports: [MatButtonModule, MatIconModule, DatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReleaseBannerComponent {
  readonly #minutesToMilliseconds = 60_000;
  readonly #maxHourOfDay = 23;
  readonly #maxMinuteOfHour = 59;

  readonly banner = input.required<IxReleaseBanner>();
  readonly dismissBanner = output<IxReleaseBanner>();
  readonly bannerType = computed(() => this.banner()?.type as ReleaseBannerType | undefined);
  readonly infoBanner = computed(() => {
    if (this.bannerType() === 'info') {
      return this.banner() as IxReleaseInfoBanner;
    } else {
      return undefined;
    }
  });
  readonly warningBanner = computed(() => {
    if (this.bannerType() === 'warning') {
      return this.banner() as IxReleaseWarningBanner;
    } else {
      return undefined;
    }
  });

  readonly localTimeZone = computed(() => Intl.DateTimeFormat().resolvedOptions().timeZone);

  readonly releaseStartsAtUtc = computed(() => {
    const warningBanner = this.warningBanner();
    if (!warningBanner) {
      return new Date(0);
    }

    const releaseDate = new Date(warningBanner.releaseDate);
    const [startHour, startMinute] = this.#parseUtcTime(warningBanner.startsAtUtc);
    if (Number.isNaN(releaseDate.getTime()) || startHour === null || startMinute === null) {
      return new Date(0);
    }

    // return exact start date time in UTC
    return new Date(
      Date.UTC(
        releaseDate.getUTCFullYear(),
        releaseDate.getUTCMonth(),
        releaseDate.getUTCDate(),
        startHour,
        startMinute,
      ),
    );
  });

  readonly releaseEndsAtUtc = computed(() => {
    const warningBanner = this.warningBanner();
    if (!warningBanner) {
      return new Date(0);
    }

    const releaseStartsAtUtc = this.releaseStartsAtUtc();
    if (releaseStartsAtUtc.getTime() === 0) {
      return new Date(0);
    }

    // calculate end date time in UTC based on start date time and duration
    return new Date(releaseStartsAtUtc.getTime() + warningBanner.duration * this.#minutesToMilliseconds);
  });

  readonly bannerStyle = computed(() => {
    switch (this.bannerType()) {
      case 'info':
        return 'release-info-banner';
      case 'warning':
        return 'release-warning-banner';
    }
  });

  readonly heading = computed(() => {
    if (this.bannerType() === 'warning') {
      return 'We are planning to release a new version of IX today';
    } else if (this.bannerType() === 'info') {
      return 'Release update';
    }
    return '';
  });

  readonly isWarning = computed(() => this.bannerType() === 'warning');
  readonly isInfo = computed(() => this.bannerType() === 'info');

  dismiss(): void {
    const banner = this.banner();
    this.dismissBanner.emit(banner);
  }

  #parseUtcTime(startsAtUtc: string): [number | null, number | null] {
    const [hourPart, minutePart] = startsAtUtc.split(':');
    const hour = Number(hourPart);
    const minute = Number(minutePart);

    if (
      !Number.isInteger(hour) ||
      !Number.isInteger(minute) ||
      hour < 0 ||
      hour > this.#maxHourOfDay ||
      minute < 0 ||
      minute > this.#maxMinuteOfHour
    ) {
      return [null, null];
    }
    return [hour, minute];
  }

  #formatLocalTime(date: Date): string {
    return new Intl.DateTimeFormat(undefined, {
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  }
}
